import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useContext, useMemo } from 'react';
import { withErrorBoundary } from '@/ErrorBoundary';
import InventoryContext from '~/contexts/InventoryContext';
import RouteContext from '~/contexts/RouteContext';
import SEOContext from '~/contexts/SEOContext';
import type { VanityUrlWithPath, Vehicle } from '~/contracts';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';

function usePageDescription(vanityUrl?: VanityUrlWithPath): string {
  return useMemo(() => {
    const prefix = 'Shop used';
    const subject = vanityUrl ? vanityUrl.keyword : 'cars';
    const location = vanityUrl?.location ? `in ${vanityUrl.location}` : '';
    const postfix =
      'for sale on Carvana. Browse used cars online & have your next vehicle delivered to your door with as soon as next day delivery.';

    return `${prefix} ${subject} ${location} ${postfix}`;
  }, [vanityUrl]);
}

function usePageTitle(
  requestUrl?: string,
  vanityUrl?: VanityUrlWithPath
): string {
  return useMemo(() => {
    const rootTitle = 'Used Cars Online | Touchless Delivery Available';
    const defaultTitle = 'Carvana | Buy Used Cars Online & Skip The Dealership';

    if (requestUrl == null) {
      return rootTitle;
    }

    if (vanityUrl == null) {
      const url = new URL(requestUrl, process.env.CVNA_APP_ABSOLUTE_BASE_URL);

      return /\/cars$/.test(url.pathname) ? rootTitle : defaultTitle;
    }

    const location = vanityUrl.location ? `in ${vanityUrl.location}` : 'Online';

    return `Used ${vanityUrl.keyword} for Sale ${location} | Carvana`;
  }, [requestUrl, vanityUrl]);
}

function getPathFromImageUrl(url = '') {
  const baseUrl = process.env.CVNA_APP_RESULT_TILE_BASE_URL || '';
  const imageUrl = new URL(url, baseUrl);
  imageUrl.search = '';
  imageUrl.hash = '';

  return url.toString();
}

function useThumbnail(vehicles: Vehicle[]) {
  return useMemo(() => {
    const firstVehicle = vehicles[0];

    return firstVehicle?.imageUrl
      ? getPathFromImageUrl(firstVehicle.imageUrl)
      : undefined;
  }, [vehicles]);
}

function HeadTags() {
  const { currentUrl } = useContext(RouteContext);
  const { vanityUrl } = useContext(SEOContext);
  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    }
  } = useContext(InventoryContext);

  const isFiltersPath = currentUrl.includes('/cars/filters');
  const pageTitle = usePageTitle(currentUrl, vanityUrl);
  const pageDescription = usePageDescription(vanityUrl);
  const thumbnail = useThumbnail(vehicles);

  const vehicleLds = vehicles.map(vehicle => ({
    id: vehicle.vehicleId,
    properties: {
      '@context': 'https://schema.org',
      '@type': 'Vehicle',
      itemCondition: 'Used',
      name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      modelDate: vehicle.year,
      manufacturer: vehicle.make,
      model: vehicle.model,
      color: vehicle.color,
      image: vehicle.imageUrl
        ? `${
            process.env.CVNA_APP_RESULT_TILE_BASE_URL ?? ''
          }${getPathFromImageUrl(vehicle.imageUrl)}`
        : undefined,
      brand: vehicle.make,
      description: `Used ${vehicle.year} ${vehicle.make} ${vehicle.model} ${
        vehicle.trim
      } with ${vehicle.mileage} miles - $${formatAndDropDecimal(
        vehicle.price.total
      )}`,
      mileageFromOdometer: vehicle.mileage,
      sku: vehicle.stockNumber,
      vehicleIdentificationNumber: vehicle.vin,
      offers: {
        '@type': 'Offer',
        price: vehicle.price.total,
        priceCurrency: 'USD',
        availability: 'http://schema.org/InStock',
        priceValidUntil: 'January 1, 2030',
        url: `${process.env.CVNA_APP_ABSOLUTE_BASE_URL}/vehicle/${vehicle.vehicleId}`
      }
    }
  }));

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={pageDescription}
        noindex={isFiltersPath}
        canonical={
          vanityUrl?.path
            ? `${process.env.CVNA_APP_ABSOLUTE_BASE_URL}/cars/${vanityUrl.path}`
            : undefined
        }
        openGraph={{
          type: 'website',
          siteName: 'Carvana',
          title: pageTitle,
          url: currentUrl,
          description: pageDescription,
          images: thumbnail ? [{ url: thumbnail }] : undefined
        }}
        additionalMetaTags={[
          {
            property: 'og:image:type',
            content: 'image/jpeg'
          }
        ]}
      />

      <Head>
        {vehicleLds.map(vehicle => (
          <script
            key={`jsonld-vehicle-${vehicle.id}`}
            type="application/ld+json"
            data-qa="vehicle-ld"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(vehicle.properties)
            }}
          />
        ))}
      </Head>
    </>
  );
}

export default withErrorBoundary(HeadTags);
